<template>
  <div class="main-content">
    <div class="section grid-wrapper">
      <cargo-ag-grid
        :config-module="configModule"
        :grid-params.sync="gridParams"
        :grid-options="gridOptions"
        :column-defs="columnDefs"
        :row-data="rowData"
      />
    </div>
  </div>
</template>

<script>
import * as PriceConstant from '@/class/cargo/model/parameters/PriceConstant'
import * as CommonState from '@/class/cargo/model/parameters/CommonState'
import { TYPE_NAMES } from '@/utils/class/AdvanceSelect'
import ColumnUtils from '@/utils/ColumnUtils'
import { PriceUtils } from '@/class/cargo/model/utils/PriceUtils'
import { cacheKeys } from '@/utils/basicDataCache'
import { Voyage } from '@/class/model/mdm/Voyage'
import { loadSpecificBasicInfo } from '@/api/cache'

export default {
  name: 'MarkupFclDataGrid',
  components: {},
  props: {
    configModule: { type: String, default: '22130202' },
    rowData: { type: Array, default: () => [] },
    editable: { type: Boolean, default: false },
    gridOptions: { type: Object, default: () => {} }
  },
  data() {
    return {
      fullscreenLoading: false,
      vesselList: [],
      voyageList: [],
      gridParams: {},
      gridApi: null,
      columnDefs: [
        {
          headerName: ' ', maxWidth: 40, checkboxSelection: true,
          sortable: false, suppressMenu: true, suppressSizeToFit: true, resizable: false,
          headerCheckboxSelection: true
        },
        { headerName: this.$t('Pricing.SubscribePOLType'), field: 'polType', colId: 'polType', width: 200, editable: this.editable,
          cellEditor: 'CargoAgGridSelectEditor',
          cellEditorParams: {
            selectProps: {
              filterable: true
            },
            options: PriceConstant.REGIONAL_TYPE,
            tagName: 'chineseName',
            tagValue: 'code'
          },
          valueFormatter: params => {
            return CommonState.getNameByCode(PriceConstant.REGIONAL_TYPE, params.value)
          },
          onCellValueChanged: (params) => {
            this.onCellChange(params)
          }
        },
        { headerName: this.$t('Pricing.SubscribePOL'), field: 'polColumn', colId: 'polColumn', width: 200, editable: this.editable,
          cellEditor: 'CargoAgGridPopSelectInput',
          cellEditorParams: params => {
            return {
              value: this.getValueByType(params, 'polType'),
              selectProps: {
                showAll: true,
                useType: params.data.polType
              }
            }
          },
          valueFormatter: params => {
            return this.polLabelFunc(params.data)
          },
          onCellValueChanged: (params) => {
            this.onCellChange(params)
          }
        },
        { headerName: this.$t('Pricing.DestinationType'), field: 'podType', colId: 'podType', width: 200, editable: this.editable,
          cellEditor: 'CargoAgGridSelectEditor',
          cellEditorParams: {
            selectProps: {
              filterable: true
            },
            options: PriceConstant.REGIONAL_TYPE,
            tagName: 'chineseName',
            tagValue: 'code'
          },
          valueFormatter: params => {
            return CommonState.getNameByCode(PriceConstant.REGIONAL_TYPE, params.value)
          },
          onCellValueChanged: (params) => {
            this.onCellChange(params)
          }
        },
        { headerName: this.$t('Pricing.Destination'), field: 'podColumn', colId: 'podColumn', width: 200, editable: this.editable,
          cellEditor: 'CargoAgGridPopSelectInput',
          cellEditorParams: params => {
            return {
              value: this.getValueByType(params, 'podType'),
              selectProps: {
                showAll: true,
                useType: params.data.podType
              }
            }
          },
          valueFormatter: params => {
            return this.podLabelFunc(params.data)
          },
          onCellValueChanged: (params) => {
            this.onCellChange(params)
          }
        },
        { headerName: this.$t('Pricing.Carrier'), field: 'carrier', colId: 'carrier', width: 200, editable: this.editable,
          cellEditor: 'CargoAgGridAdvanceSelectEditor',
          cellEditorParams: {
            selectProps: {
              selectType: TYPE_NAMES.basic_data_Company,
              methodParams: { seaCmp: true },
              showCustomLabelInOption: true,
              customLabel: (item) => `[${item.shortName}]${item.chineseName}`,
              filterable: true,
              clearable: false
            }
          },
          valueFormatter: params => {
            return this.dataGridColumnCarrierFunc(params.value)
          }
        },
        { headerName: this.$t('Pricing.DepartureDay'), field: 'frequency', colId: 'frequency', width: 200, editable: this.editable,
          cellEditor: 'CargoAgGridSelectEditor',
          cellEditorParams: {
            selectProps: {
              filterable: true
            },
            options: PriceConstant.DEPARTURE_DAY,
            tagName: 'chineseName',
            tagValue: 'code'
          },
          valueFormatter: params => {
            return CommonState.getNameByCode(PriceConstant.DEPARTURE_DAY, params.value)
          },
          onCellValueChanged: (params) => {
            this.onCellChange(params)
          }
        },
        { headerName: this.$t('Pricing.PriceType'), field: 'freightType', colId: 'freightType', width: 200, editable: this.editable,
          cellEditor: 'CargoAgGridSelectEditor',
          cellEditorParams: {
            selectProps: {
              filterable: true
            },
            options: PriceConstant.PRICE_TYPES,
            tagName: 'chineseName',
            tagValue: 'code'
          },
          valueFormatter: params => {
            if (!params.value && params.value !== 0) {
              return this.$t('Pricing.ALL')
            }
            return CommonState.getNameByCode(PriceConstant.PRICE_TYPES, params.value)
          },
          onCellValueChanged: (params) => {
            this.onCellChange(params)
          }
        },
        { headerName: this.$t('SeaExport.Vessel'), field: 'vessel', colId: 'vessel', width: 200, editable: this.editable,
          cellEditor: 'CargoAgGridAdvanceSelectEditor',
          cellEditorParams: (params) => ({
            selectProps: {
              tagValue: 'englishName',
              tagName: 'englishName',
              allowCreate: true,
              staticOptions: this.vesselList,
              filterable: true
            }
          }),
          onCellValueChanged: (params) => {
            this.onCellChange(params)
          },
          valueFormatter: params => {
            if (!params.value && params.value !== 0) {
              return this.$t('Pricing.ALL')
            }
            return params.value
          }
        },
        { headerName: this.$t('SeaExport.Voyage'), field: 'voyageFlight', colId: 'voyageFlight', width: 200, editable: this.editable,
          cellEditor: 'CargoAgGridAdvanceSelectEditor',
          cellEditorParams: (params) => {
            const vesselId = ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_Vessel, 'englishName', params.data.vessel, 'vesselId')
            const voyages = this.voyageList.filter(item => item.vesselId === vesselId)
            return {
              selectProps: {
                cache: false,
                staticOptions: voyages,
                filterable: true,
                tagName: 'voyageNo',
                allowCreate: true,
                tagValue: 'voyageNo'
              }
            }
          },
          onCellValueChanged: (params) => {
            this.onCellChange(params)
          },
          valueFormatter: params => {
            if (!params.value && params.value !== 0) {
              return this.$t('Pricing.ALL')
            }
            return params.value
          }
        },
        { headerName: 'Cost ' + this.$t('Pricing.MarkupType'), field: 'isAddByCost', colId: 'isAddByCost', width: 200, editable: this.editable,
          cellEditor: 'CargoAgGridSelectEditor',
          cellEditorParams: {
            selectProps: {
              filterable: true
            },
            options: PriceConstant.MARKUP_TYPE,
            tagName: 'chineseName',
            tagValue: 'code'
          },
          valueFormatter: params => {
            return CommonState.getNameByCode(PriceConstant.MARKUP_TYPE, params.value)
          },
          onCellValueChanged: (params) => {
            this.onCellChange(params)
          } },
        { headerName: "20'Cost", field: 'cost20', colId: 'cost20', width: 200, editable: this.editable,
          cellEditor: 'CargoAgGridCommonInputEditor',
          cellEditorParams: { isNumber: true },
          onCellValueChanged: (params) => this.onCellChange(params)
        },
        { headerName: "40'Cost", field: 'cost40', colId: 'cost40', width: 200, editable: this.editable,
          cellEditor: 'CargoAgGridCommonInputEditor',
          cellEditorParams: { isNumber: true },
          onCellValueChanged: (params) => this.onCellChange(params)
        },
        { headerName: "40'HC Cost", field: 'costHC', colId: 'costHC', width: 200, editable: this.editable,
          cellEditor: 'CargoAgGridCommonInputEditor',
          cellEditorParams: { isNumber: true },
          onCellValueChanged: (params) => this.onCellChange(params)
        },
        { headerName: "20'NOR Cost", field: 'cost20nor', colId: 'cost20nor', width: 200, editable: this.editable,
          cellEditor: 'CargoAgGridCommonInputEditor',
          cellEditorParams: { isNumber: true },
          onCellValueChanged: (params) => this.onCellChange(params)
        },
        { headerName: "40'NOR Cost", field: 'cost40nor', colId: 'cost40nor', width: 200, editable: this.editable,
          cellEditor: 'CargoAgGridCommonInputEditor',
          cellEditorParams: { isNumber: true },
          onCellValueChanged: (params) => this.onCellChange(params)
        },
        { headerName: "45' Cost", field: 'cost45', colId: 'cost45', width: 200, editable: this.editable,
          cellEditor: 'CargoAgGridCommonInputEditor',
          onCellValueChanged: (params) => this.onCellChange(params)
        },
        { headerName: 'T1 ' + this.$t('Pricing.MarkupType'), field: 'isAddBy1', colId: 'isAddBy1', width: 200, editable: this.editable,
          cellEditor: 'CargoAgGridSelectEditor',
          cellEditorParams: {
            selectProps: {
              filterable: true
            },
            options: PriceConstant.MARKUP_TYPE,
            tagName: 'chineseName',
            tagValue: 'code'
          },
          valueFormatter: params => {
            return CommonState.getNameByCode(PriceConstant.MARKUP_TYPE, params.value)
          },
          onCellValueChanged: (params) => {
            this.onCellChange(params)
          } },
        { headerName: "20'T1", field: 'one20', colId: 'one20', width: 200, editable: this.editable,
          cellEditor: 'CargoAgGridCommonInputEditor',
          cellEditorParams: { isNumber: true },
          onCellValueChanged: (params) => this.onCellChange(params)
        },
        { headerName: "40'T1", field: 'one40', colId: 'one40', width: 200, editable: this.editable,
          cellEditor: 'CargoAgGridCommonInputEditor',
          cellEditorParams: { isNumber: true },
          onCellValueChanged: (params) => this.onCellChange(params)
        },
        { headerName: "40'HC T1", field: 'oneHC', colId: 'oneHC', width: 200, editable: this.editable,
          cellEditor: 'CargoAgGridCommonInputEditor',
          cellEditorParams: { isNumber: true },
          onCellValueChanged: (params) => this.onCellChange(params)
        },
        { headerName: "20'NOR T1", field: 'one20nor', colId: 'one20nor', width: 200, editable: this.editable,
          cellEditor: 'CargoAgGridCommonInputEditor',
          cellEditorParams: { isNumber: true },
          onCellValueChanged: (params) => this.onCellChange(params)
        },
        { headerName: "40'NOR T1", field: 'one40nor', colId: 'one40nor', width: 200, editable: this.editable,
          cellEditor: 'CargoAgGridCommonInputEditor',
          cellEditorParams: { isNumber: true },
          onCellValueChanged: (params) => this.onCellChange(params)
        },
        { headerName: "45' T1", field: 'one45', colId: 'one45', width: 200, editable: this.editable,
          cellEditor: 'CargoAgGridCommonInputEditor',
          onCellValueChanged: (params) => this.onCellChange(params)
        },
        { headerName: 'T2 ' + this.$t('Pricing.MarkupType'), field: 'isAddBy2', colId: 'isAddBy2', width: 200, editable: this.editable,
          cellEditor: 'CargoAgGridSelectEditor',
          cellEditorParams: {
            selectProps: {
              filterable: true
            },
            options: PriceConstant.MARKUP_TYPE,
            tagName: 'chineseName',
            tagValue: 'code'
          },
          valueFormatter: params => {
            return CommonState.getNameByCode(PriceConstant.MARKUP_TYPE, params.value)
          },
          onCellValueChanged: (params) => {
            this.onCellChange(params)
          } },
        { headerName: "20'T2", field: 'two20', colId: 'two20', width: 200, editable: this.editable,
          cellEditor: 'CargoAgGridCommonInputEditor',
          cellEditorParams: { isNumber: true },
          onCellValueChanged: (params) => this.onCellChange(params)
        },
        { headerName: "40'T2", field: 'two40', colId: 'two40', width: 200, editable: this.editable,
          cellEditor: 'CargoAgGridCommonInputEditor',
          cellEditorParams: { isNumber: true },
          onCellValueChanged: (params) => this.onCellChange(params)
        },
        { headerName: "40'HC T2", field: 'twoHC', colId: 'twoHC', width: 200, editable: this.editable,
          cellEditor: 'CargoAgGridCommonInputEditor',
          cellEditorParams: { isNumber: true },
          onCellValueChanged: (params) => this.onCellChange(params)
        },
        { headerName: "20'NOR T2", field: 'two20nor', colId: 'two20nor', width: 200, editable: this.editable,
          cellEditor: 'CargoAgGridCommonInputEditor',
          cellEditorParams: { isNumber: true },
          onCellValueChanged: (params) => this.onCellChange(params)
        },
        { headerName: "40'NOR T2", field: 'two40nor', colId: 'two40nor', width: 200, editable: this.editable,
          cellEditor: 'CargoAgGridCommonInputEditor',
          cellEditorParams: { isNumber: true },
          onCellValueChanged: (params) => this.onCellChange(params)
        },
        { headerName: "45' T2", field: 'two45', colId: 'two45', width: 200, editable: this.editable,
          cellEditor: 'CargoAgGridCommonInputEditor',
          cellEditorParams: { isNumber: true },
          onCellValueChanged: (params) => this.onCellChange(params)
        },
        { headerName: 'T3 ' + this.$t('Pricing.MarkupType'), field: 'isAddBy3', colId: 'isAddBy3', width: 200, editable: this.editable,
          cellEditor: 'CargoAgGridSelectEditor',
          cellEditorParams: {
            selectProps: {
              filterable: true
            },
            options: PriceConstant.MARKUP_TYPE,
            tagName: 'chineseName',
            tagValue: 'code'
          },
          valueFormatter: params => {
            return CommonState.getNameByCode(PriceConstant.MARKUP_TYPE, params.value)
          },
          onCellValueChanged: (params) => {
            this.onCellChange(params)
          } },
        { headerName: "20'T3", field: 'three20', colId: 'three20', width: 200, editable: this.editable,
          cellEditor: 'CargoAgGridCommonInputEditor',
          cellEditorParams: { isNumber: true },
          onCellValueChanged: (params) => this.onCellChange(params)
        },
        { headerName: "40'T3", field: 'three40', colId: 'three40', width: 200, editable: this.editable,
          cellEditor: 'CargoAgGridCommonInputEditor',
          cellEditorParams: { isNumber: true },
          onCellValueChanged: (params) => this.onCellChange(params)
        },
        { headerName: "40'HC T3", field: 'threeHC', colId: 'threeHC', width: 200, editable: this.editable,
          cellEditor: 'CargoAgGridCommonInputEditor',
          cellEditorParams: { isNumber: true },
          onCellValueChanged: (params) => this.onCellChange(params)
        },
        { headerName: "20'NOR T3", field: 'three20nor', colId: 'three20nor', width: 200, editable: this.editable,
          cellEditor: 'CargoAgGridCommonInputEditor',
          cellEditorParams: { isNumber: true },
          onCellValueChanged: (params) => this.onCellChange(params)
        },
        { headerName: "40'NOR T3", field: 'three40nor', colId: 'three40nor', width: 200, editable: this.editable,
          cellEditor: 'CargoAgGridCommonInputEditor',
          cellEditorParams: { isNumber: true },
          onCellValueChanged: (params) => this.onCellChange(params)
        },
        { headerName: "45' T3", field: 'three45', colId: 'three45', width: 200, editable: this.editable,
          cellEditor: 'CargoAgGridCommonInputEditor',
          cellEditorParams: { isNumber: true },
          onCellValueChanged: (params) => this.onCellChange(params)
        }
      ]
    }
  },
  computed: {
    showMainList() {
      return !this.showDetail
    },
    language() {
      return this.$store.getters.language === 'zh' ? 'chineseName' : 'englishName'
    }
  },
  watch: {
  },
  async mounted() {
    this.gridOptions.onRowClicked = this.onRowClickedEvent
    this.vesselList = await loadSpecificBasicInfo('Vessel').then(res => res.data)
    this.voyageList = await loadSpecificBasicInfo('Voyage').then(res => res.data)
  },
  methods: {
    async init() {
    },
    onCellChange(params) {
      const { node } = params
      if (node.data.uuid) {
        node.data.dirty = true
      }
      // 处理价格，当无值时，默认为0
      const field = params.colDef.field
      if (['polType', 'podType', 'polColumn', 'podColumn'].indexOf(field) > -1) {
        if (params.colDef.field === 'polColumn') {
          this.setValueByType(params, 'polType')
        }
        if (params.colDef.field === 'podColumn') {
          this.setValueByType(params, 'podType')
        }
        this.gridOptions.api.refreshCells({ force: true })
        return
      }
      if (['cost20', 'cost40', 'costHC', 'cost20nor', 'cost40nor', 'cost45',
        'one20', 'one40', 'oneHC', 'one20nor', 'one40nor', 'one45',
        'two20', 'two40', 'twoHC', 'two20nor', 'two40nor', 'two45',
        'three20', 'three40', 'threeHC', 'three20nor', 'three40nor', 'three45'].indexOf(field) > -1) {
        if (params.data[field] === '') {
          params.data[field] = 0
          this.gridOptions.api.refreshCells({ force: true })
        }
      }
    },
    setBooleanValue(field) {
      if (this.filter[field]) {
        this.proxy[field] = this.filter[field]
      }
    },
    filterDataGridColumnCarrierFunc(value) {
      const targetFields = ['shortName', 'chineseName', 'englishName']
      let result = ''
      for (const targetField of targetFields) {
        result += ColumnUtils.companyLabelFun(value, targetField, 'cmpIdx')
      }
      return result
    },
    dataGridColumnCarrierFunc(value) {
      if (!value || value === 0) {
        return this.$t('Pricing.ALL')
      }
      return ColumnUtils.companyLabelFun(value, 'shortName', 'cmpIdx')
    },
    filterPolLabelFunc(data) {
      switch (data.polType) {
        case 0:
          return this.getRegionFilter(data['rol'])
        case 1:
          return this.getCountryLabel(data, 'col')
        case 2:
          return this.getPortLabel(data, 'pol')
        default:
          return '【NIL】'
      }
    },
    getRegionFilter(value) {
      const values = value.split(',')
      const result = []
      for (const value1 of values) {
        result.push(this.getRegionValue(value1))
      }
      return result.join()
    },
    getRegionValue(value) {
      const result = ColumnUtils.getBasicDataCacheList(cacheKeys.basic_data_Region, item => item.code === value)
      if (!result || result.length === 0) {
        return ''
      }
      const region = result ? result[0] : null
      return region.code + region.name + region.nameEnglish
    },
    polLabelFunc(item) {
      switch (item.polType) {
        case 0:
          return this.getRegionLabel(item, 'rol')
        case 1:
          return this.getCountryLabel(item, 'col')
        case 2:
          return this.getPortLabel(item, 'pol')
        default:
          return '【NIL】'
      }
    },
    filterPodLabelFunc(item) {
      switch (item.podType) {
        case 0:
          return this.getRegionFilter(item['rod'])
        case 1:
          return this.getCountryLabel(item, 'cod')
        case 2:
          return this.getPortLabel(item, 'pod')
        default:
          return '【NIL】'
      }
    },
    podLabelFunc(item) {
      switch (item.podType) {
        case 0:
          return this.getRegionLabel(item, 'rod')
        case 1:
          return this.getCountryLabel(item, 'cod')
        case 2:
          return this.getPortLabel(item, 'pod')
        default:
          return '【NIL】'
      }
    },
    getRegionLabel(item, field) {
      const language = this.$store.getters.language === 'zh' ? 'name' : 'nameEnglish'
      return this.getLabel(item, field, language, TYPE_NAMES.basic_data_Region)
    },
    getCountryLabel(item, field) {
      const language = this.$store.getters.language === 'zh' ? 'chineseName' : 'name'
      return this.getLabel(item, field, language, TYPE_NAMES.basic_data_Country)
    },
    getPortLabel(item, field) {
      return this.getLabel(item, field, 'code', TYPE_NAMES.basic_data_Seaport)
    },
    getLabel(item, field, sourceLanguage, cacheKey) {
      if (!item[field]) return ''
      const values = item[field].split(',')
      const valueName = []
      for (const value of values) {
        if (item[field] === 'ALLRG') {
          valueName.push(this.$t('Pricing.ALLRG'))
          continue
        }
        const labelName = ColumnUtils.resolveColumnLabel(cacheKey, 'code', value, sourceLanguage)
        valueName.push(labelName)
      }
      return valueName.join()
    },
    onRowClickedEvent(e) {
      const data = e.data
      this.$emit('selectRowChange', data) // 行选中回调
    },
    getValueByType(params, fieldType) {
      const field = this.getField(params, fieldType)
      if (params.data.hasOwnProperty(field)) {
        return params.data[field]
      }
      return params.value
    },
    setValueByType(params, field) {
      const changeField = this.getField(params, field)
      if (!params.data.hasOwnProperty(changeField)) {
        return false
      }
      params.data[changeField] = params.newValue
      return true
    },
    getField(params, typeField) {
      const value = params.data[typeField]
      if (typeField === 'polType') {
        return PriceUtils.getPolField(value)
      }
      if (typeField === 'podType') {
        return PriceUtils.getPodField(value)
      }
    },
    async getVoyageByVessel(vesselId) {
      if (vesselId) {
        this.voyages = await Voyage.getVoyage(vesselId)
      } else {
        this.voyages = []
      }
    },
    loadSelectRows() {
      return this.gridOptions.api.getSelectedRows()
    }
  }
}
</script>
<style lang="scss">
</style>

